import React, { useEffect, useMemo, useState } from 'react'
import './CalendarComponent.css'
import { motion } from 'framer-motion'
import axios from 'axios'
import { showErrorNotification } from '../services/notificationService'

const DAYS = ['Lunes','Martes','Miercoles','Jueves','Viernes','Sabado','Domingo']

// Pastel day accents (header chip + time pill border)
const DAY_ACCENT = {
  Lunes:     '#8ec5ff',
  Martes:    '#9fe3ff',
  Miercoles: '#b8e0ff',
  Jueves:    '#cde8ff',
  Viernes:   '#8fb7ff',
  Sabado:    '#6ea6ff',
  Domingo:   '#5b7cff',
}

// Robust 24h → 12h (works with "HH:mm" or "HH:mm:ss")
const to12h = (time24 = '') => {
  const [hStr, mStr] = time24.split(':')
  const h = Number(hStr || 0)
  const m = Number(mStr || 0)
  const suf = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  const pad2 = (n) => String(n).padStart(2, '0')
  return `${h12}:${pad2(m)} ${suf}`
}

const CalendarComponent = () => {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const url = `${process.env.REACT_APP_BASE_URL}/api/events/`
        const { data } = await axios.get(url, { timeout: 5000 })
        setEvents(Array.isArray(data) ? data : [])
      } catch (err) {
        showErrorNotification(err)
      } finally {
        setLoading(false)
      }
    }
    fetchEvents()
  }, [])

  // Group & sort
  const schedule = useMemo(() => {
    const byDay = Object.fromEntries(DAYS.map(d => [d, []]))
    for (const ev of events) {
      if (byDay[ev.day]) byDay[ev.day].push(ev)
    }
    for (const d of DAYS) {
      byDay[d].sort((a, b) => (a.startTime > b.startTime ? 1 : -1))
    }
    return byDay
  }, [events])

  return (
    <div className="yc">
      {/* HERO */}
      <section className="yc__hero">
        <div className="yc__hero-body">
          <h1>Horarios de clases</h1>
          <p>
            Te esperamos en nuestro estudio en Guadalajara. Disfruta clases presenciales
            en un ambiente sereno y acogedor.
          </p>
        </div>
      </section>

      {/* GRID */}
      <section className="yc__shell">
        {loading ? (
          <div className="yc__grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="yc__day yc--skeleton">
                <div className="yc__day-head skeleton" />
                <div className="yc__class skeleton" />
                <div className="yc__class skeleton" />
                <div className="yc__class skeleton" />
              </div>
            ))}
          </div>
        ) : (
          <div className="yc__grid">
            {DAYS.map((day) => (
              <motion.div
                key={day}
                className="yc__day"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35 }}
              >
                <header
                  className="yc__day-head"
                  style={{
                    '--day-accent': DAY_ACCENT[day],
                  }}
                >
                  <span className="yc__chip">{day}</span>
                </header>

                {schedule[day].length === 0 ? (
                  <div className="yc__empty">Sin clases por ahora</div>
                ) : (
                  <ul className="yc__list">
                    {schedule[day].map(({ id, title, description, startTime, endTime }) => (
                      <li key={id} className="yc__class">
                        <div
                          className="yc__time"
                          style={{ '--day-accent': DAY_ACCENT[day] }}
                          title={`${startTime} - ${endTime}`}
                        >
                          {to12h(startTime)} – {to12h(endTime)}
                        </div>
                        <div className="yc__content">
                          <h4 className="yc__title">{title}</h4>
                          {description && <p className="yc__desc">{description}</p>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* CONTACT */}
      <section className="yc__footer">
        <motion.div
          className="yc__card"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
        >
          <h3>Contáctanos</h3>
          <ul className="yc__contact">
            <li><strong>Teléfono:</strong> (33) 14183347</li>
            <li><strong>Dirección:</strong> Plateros 2378, Jardines del Country, GDL, Jalisco</li>
            <li><strong>Contacto:</strong> Sandra López</li>
          </ul>
        </motion.div>

        <motion.div
          className="yc__map"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.05 }}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14927.981747874976!2d-103.3675171!3d20.7104099!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8428ae2d2e01737b%3A0xb903826fbd009741!2sClases%20de%20Yoga%20Sat%20Nam%20Estudio!5e0!3m2!1ses-419!2smx!4v1706934359712!5m2!1ses-419!2smx"
            width="100%"
            height="360"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Ubicación Sat Nam Estudio"
          />
        </motion.div>
      </section>
    </div>
  )
}

export default CalendarComponent
